/**
 * A pergunta "esta conversa tem rascunho local que o motor não conhece?", sem
 * nada do pacote compartilhado — é o que o `sendRaw` consulta a CADA envio, e
 * por isso mora no pedaço de boot.
 */
import { useAuthStore } from '@/stores/authStore'
import {
  useEquipmentRegistrationFlowStore,
  type RascunhoDeCadastro,
} from '@/stores/equipmentRegistrationFlowStore'

/** `tenantId|userId` da sessão, ou `null` antes do Clerk resolver a organização. */
export function donoAtual(): string | null {
  const { tenantId, userId } = useAuthStore.getState()
  return tenantId && userId ? `${tenantId}|${userId}` : null
}

/** Rascunhos do dono atual nesta conversa com cliques que o servidor ainda não refez. */
export function rascunhosPendentes(conversationId: string | null): RascunhoDeCadastro[] {
  const dono = donoAtual()
  return Object.values(useEquipmentRegistrationFlowStore.getState().rascunhos).filter(
    (r) => r.conversationId === conversationId && r.dono === dono && r.acoes.length > 0,
  )
}

/** Síncrono, para o envio comum não ganhar um `await` (nem um tique) a mais. */
export function precisaSincronizar(conversationId: string | null): boolean {
  return rascunhosPendentes(conversationId).length > 0
}

/**
 * Envio que NÃO é do compositor com rascunho vivo (spec §2.4, decisão do tech
 * lead na 4ª revisão de #1420): o rascunho sai e o cartão volta ao snapshot do
 * servidor com "os dados mudaram"; o envio segue normal e síncrono. Os passos
 * locais só se perdem se a pessoa sai do cartão no meio por outro controle.
 */
export function descartarRascunhosPendentes(conversationId: string | null): void {
  const store = useEquipmentRegistrationFlowStore.getState()
  for (const r of rascunhosPendentes(conversationId)) {
    store.aplicarRecibo(r.flowId, null)
    store.marcarEnvio(r.flowId, {
      fase: 'falhou',
      falha: { tipo: 'conflito', codigo: 'RASCUNHO_VELHO' },
    })
  }
}

/**
 * A fila de envios do COMPOSITOR de uma conversa enquanto há rascunho local a sincronizar:
 * cada envio espera o anterior (o sync e o turno dele), então os turnos saem
 * na ordem em que a pessoa os fez, e uma janela de sync tem UM POST só.
 */
const filas = new Map<string | null, Promise<unknown>>()

/** O envio tem de esperar: há rascunho a sincronizar, ou há envios na fila desta conversa. */
export function precisaEsperar(conversationId: string | null): boolean {
  return precisaSincronizar(conversationId) || filas.has(conversationId)
}

export function naFila<T>(conversationId: string | null, tarefa: () => Promise<T>): Promise<T> {
  const anterior = filas.get(conversationId) ?? Promise.resolve()
  const esta = anterior.then(tarefa)
  const fim = esta.catch(() => undefined)
  filas.set(conversationId, fim)
  void fim.then(() => {
    if (filas.get(conversationId) === fim) filas.delete(conversationId)
  })
  return esta
}
